import { useCallback, useEffect, useState } from 'react'
import type {
  AudioAnalysisSummary
} from '../../../api'
import type {
  AudioTrackLabelSaveInput,
  AudioTrackLabelSuggestion,
  AudioTrackLabelStateResult
} from '../../../api/labels'

type UseTrackLabelSuggestionsInput = {
  currentStylePrompt: string
  labelIds: string[]
  loadTrackLabels: () => Promise<AudioTrackLabelStateResult>
  onAnalyzeAudio: (trackId: string) => Promise<AudioAnalysisSummary>
  onApplySuggestions: (items: AudioTrackLabelSaveInput[]) => void
  onSetAudioAnalysis: (summary: AudioAnalysisSummary) => void
  onSuggestLabels: (trackId: string, stylePrompt: string) => Promise<AudioTrackLabelSuggestion[]>
  suggestionsDialogOpen: boolean
  trackId: string
}

export const getAudioAnalysisMessage = (summary: AudioAnalysisSummary): string => {
  if (summary.status === 'never_run') {
    return 'Essentia label analysis has not run for this track.'
  }

  if (summary.status === 'queued') {
    return 'Essentia label analysis is queued.'
  }

  if (summary.status === 'processing') {
    return 'Essentia label analysis is running.'
  }

  if (summary.status === 'failed') {
    return summary.last_error ? `Essentia label analysis failed: ${summary.last_error}` : 'Essentia label analysis failed.'
  }

  if (summary.pending_suggestion_count > 0) {
    return 'Essentia label analysis completed with new suggestions.'
  }

  if (summary.rejected_suggestion_count > 0) {
    return 'Essentia label analysis completed. Suggested labels were already rejected.'
  }

  return 'Essentia label analysis completed. No new labels were found.'
}

export const useTrackLabelSuggestions = ({
  currentStylePrompt,
  labelIds,
  loadTrackLabels,
  onAnalyzeAudio,
  onApplySuggestions,
  onSetAudioAnalysis,
  onSuggestLabels,
  suggestionsDialogOpen,
  trackId
}: UseTrackLabelSuggestionsInput) => {
  const [audioAnalysisPolling, setAudioAnalysisPolling] = useState(false)
  const [audioAnalysisMessage, setAudioAnalysisMessage] = useState<string | null>(null)
  const [labelSuggestions, setLabelSuggestions] = useState<AudioTrackLabelSuggestion[]>([])
  const [selectedSuggestionIds, setSelectedSuggestionIds] = useState<string[]>([])
  const [loadingSuggestions, setLoadingSuggestions] = useState(false)
  const [suggestionError, setSuggestionError] = useState<string | null>(null)

  const canSuggestLabels = Boolean(currentStylePrompt) && !loadingSuggestions

  const suggestLabels = useCallback(async (): Promise<void> => {
    setLoadingSuggestions(true)
    setSuggestionError(null)

    try {
      const suggestions = await onSuggestLabels(trackId, currentStylePrompt)
      const newSuggestions = suggestions.filter((suggestion) => !labelIds.includes(suggestion.label_id))

      setLabelSuggestions(newSuggestions)
      setSelectedSuggestionIds(newSuggestions.map((suggestion) => suggestion.label_id))

      if (newSuggestions.length === 0) {
        setSuggestionError('No new label suggestions returned.')
      }
    } catch (error) {
      setLabelSuggestions([])
      setSelectedSuggestionIds([])
      setSuggestionError(error instanceof Error ? error.message : 'Failed to suggest labels.')
    } finally {
      setLoadingSuggestions(false)
    }
  }, [currentStylePrompt, labelIds, onSuggestLabels, trackId])

  const analyzeAudio = useCallback(async (): Promise<void> => {
    setAudioAnalysisMessage(null)
    setAudioAnalysisPolling(true)
    try {
      const summary = await onAnalyzeAudio(trackId)
      onSetAudioAnalysis(summary)
      await loadTrackLabels()
      setAudioAnalysisMessage('Essentia label analysis queued. Results will appear here automatically.')
    } catch (error) {
      setAudioAnalysisPolling(false)
      setAudioAnalysisMessage(error instanceof Error ? error.message : 'Failed to queue audio analysis.')
    }
  }, [loadTrackLabels, onAnalyzeAudio, onSetAudioAnalysis, trackId])

  const applySelectedSuggestions = useCallback((): void => {
    const existingIds = new Set(labelIds)
    const additions = labelSuggestions
      .filter((suggestion) => selectedSuggestionIds.includes(suggestion.label_id) && !existingIds.has(suggestion.label_id))
      .map((suggestion) => ({
        labelId: suggestion.label_id,
        source: 'prompt_llm' as const,
        confidence: suggestion.confidence,
        weight: suggestion.weight,
        reason: suggestion.reason
      }))

    if (additions.length > 0) {
      onApplySuggestions(additions)
    }

    setLabelSuggestions((current) => current.filter((suggestion) => !selectedSuggestionIds.includes(suggestion.label_id)))
    setSelectedSuggestionIds([])
    setSuggestionError(null)
  }, [labelIds, labelSuggestions, onApplySuggestions, selectedSuggestionIds])

  useEffect(() => {
    if (!audioAnalysisPolling || !suggestionsDialogOpen) {
      return undefined
    }

    let attempts = 0
    const poll = async (): Promise<void> => {
      attempts += 1
      const result = await loadTrackLabels()
      const summary = result.audio_analysis

      if (summary.status === 'completed' || summary.status === 'failed') {
        setAudioAnalysisPolling(false)
        setAudioAnalysisMessage(getAudioAnalysisMessage(summary))
        return
      }

      if (attempts >= 18) {
        setAudioAnalysisPolling(false)
        setAudioAnalysisMessage('No new audio suggestions yet. You can keep this window open and check again.')
      }
    }

    const intervalId = window.setInterval(() => {
      void poll()
    }, 5000)

    return () => window.clearInterval(intervalId)
  }, [audioAnalysisPolling, loadTrackLabels, suggestionsDialogOpen])

  return {
    analyzeAudio,
    applySelectedSuggestions,
    audioAnalysisMessage,
    audioAnalysisPolling,
    canSuggestLabels,
    labelSuggestions,
    loadingSuggestions,
    selectedSuggestionIds,
    setSelectedSuggestionIds,
    suggestionError,
    suggestLabels
  }
}
